/**
 * The Thrive context blocks inside a contact's prompt block (prompt 2.1.0+):
 * `<thrive_notes data-nonce="…">…</thrive_notes data-nonce="…">` and, since
 * 2.2.0, the same shape for `<thrive_assessments>`. The person panel's run
 * card jumps to them ("4 Thrive notes" / "1 assessment"), so the Request tab
 * renders them as their own spans.
 *
 * The markers are reliable: note and assessment text is escaped when the
 * prompt is built (`prompt.py::_content`), so a note cannot contain a raw
 * `<thrive_notes` or `</thrive_notes`. Both tags carry the candidate's nonce,
 * so a closing tag is matched up to its own `>`.
 */

export type ContextSpanKind = "notes" | "assessments";

export interface ContextSegment {
  kind: "plain" | ContextSpanKind;
  text: string;
}

const KIND_OF_TAG: Record<string, ContextSpanKind> = {
  thrive_notes: "notes",
  thrive_assessments: "assessments",
};

/** One block, opening tag to the end of its own closing tag. An opening tag
 *  with no closing tag is left as plain text. */
const BLOCK = /<(thrive_notes|thrive_assessments)(?:\s[^>]*)?>[\s\S]*?<\/\1(?:\s[^>]*)?>/g;

/** `text` cut into plain runs and context blocks, in order; joined back
 *  together the segments are `text` exactly. A prompt with no context (2.0.0,
 *  or a contact Thrive knows nothing about) is one plain segment. */
export function splitContextSpans(text: string): ContextSegment[] {
  const segments: ContextSegment[] = [];
  let at = 0;
  for (const match of text.matchAll(BLOCK)) {
    const start = match.index;
    const kind = KIND_OF_TAG[match[1] ?? ""];
    if (kind === undefined) continue;
    if (start > at) segments.push({ kind: "plain", text: text.slice(at, start) });
    segments.push({ kind, text: match[0] });
    at = start + match[0].length;
  }
  if (at < text.length || segments.length === 0) {
    segments.push({ kind: "plain", text: text.slice(at) });
  }
  return segments;
}
